import React, { useEffect, useState } from 'react';
import { Routes as ReactRoutes, Route, useLocation } from 'react-router-dom';
import { storage } from '@uhg-abyss/web/tools/storage';
import { AppWrapper } from '@src/app/AppWrapper';
import { MaintenanceWidget } from '@sde/widgets-core/src/widgets/MaintenanceWidget';
import { getFeature } from '@sde/widgets-core/src/redux/tools/features';
import { NotFound } from '@src/common/NotFound';
import { useUser } from '@sde/widgets-core/src/redux/resources/user';
import { useProvider } from '@src/state/provider';
import { useBreadcrumbs } from '@src/state/breadcrumbs';
import { usePayer } from '@src/state/payer';
import { useDashboard } from '@src/state/dashboard';
import { useModal } from '@src/state/modal';
import { usePayers } from '@sde/widgets-core/src/redux/resources/payers';
import { useOnboarding } from '@sde/widgets-core/src/redux/resources/onboarding';
import { PRODUCT_DOMAINS, getTitle } from '../tools/routesTools';
import { Landing } from './Home/Content/routes/Landing';
import { CoveoSearchPage } from '../../src/routes/Home/Content/routes/AppSearch/routes/CoveoSearchPage/CoveoSearchPage';
import { AdditionalTools } from './Home/Content/routes/AdditionalTools';
import { ContactUs } from './Home/Content/routes/ContactUs';
import { OptOut } from './Home/Content/routes/OptOut';
import { TechnicalUserRedirect } from './TechnicalUserRedirect';

export const Routes = () => {
  const [digitalSecurityError, setDigitalSecurityError] = useState({
    code: 'none',
    message: 'none',
    isDigitalSecurityError: false,
    isProviderPreferenceError: false,
  });
  const { setInitBreadcrumbs } = useBreadcrumbs();
  const { user } = useUser.state();
  const { provider } = useProvider.state();
  const { payer } = usePayer.state();
  const { payers } = usePayers.state();
  const { providerPreferenceComplete } = useDashboard.state();
  const [isContentLoading, setIsContentLoading] = useState(true);
  const { modals, modalData } = useModal.state();
  const { dashboard } = useDashboard.state();
  const onboarding = useOnboarding.state();

  const location = useLocation();

  if (getFeature('SDE_CORE_MAINTENANCE_PAGE')) {
    return <MaintenanceWidget />;
  }

  useEffect(() => {
    storage.session.remove('breadcrumbs');
    setInitBreadcrumbs(user.isTechnicalUser);
  }, [user.isTechnicalUser]);

  useEffect(() => {
    const pageTitle = getTitle(location.pathname);
    document.title = `${pageTitle} | Secure Provider Portal`;
  }, [location.pathname]);

  const stringifiedUser = JSON.stringify({ ...user });
  const stringifiedProvider = JSON.stringify({ ...provider });
  const stringifiedPayer = JSON.stringify({ ...payer });
  const stringifiedPayers = JSON.stringify([...payers]);
  const strigifiedModalData = JSON.stringify({ ...modalData });
  const stringifiedModals = JSON.stringify({ ...modals });
  const stringifiedDashboard = JSON.stringify({ ...dashboard });
  const stringifiedOnboarding = JSON.stringify({ ...onboarding });

  const sharedParcelProps = {
    key: location.pathname,
    user: stringifiedUser,
    provider: stringifiedProvider,
    payer: stringifiedPayer,
    'provider-preference-complete': providerPreferenceComplete,
    onboarding: stringifiedOnboarding,
    'modal-data': strigifiedModalData,
    modals: stringifiedModals,
    payers: stringifiedPayers,
    dashboard: stringifiedDashboard,
  };

  return (
    <ReactRoutes>
      <Route
        path='/'
        element={
          <AppWrapper
            isContentLoading={isContentLoading}
            setIsContentLoading={setIsContentLoading}
            digitalSecurityError={digitalSecurityError}
            setDigitalSecurityError={setDigitalSecurityError}
          />
        }
      >
        {!isContentLoading && (
          <Route
            path='/'
            element={<Landing digitalSecurityError={digitalSecurityError} />}
          />
        )}

        <Route
          path='/prefcenter/*'
          element={
            <abyss-parcel
              scriptdomain={PRODUCT_DOMAINS['prefcenter']}
              import='prefcenter-product@dev'
              {...sharedParcelProps}
            />
          }
        />
        <Route
          path='/claims/*'
          element={
            <abyss-parcel
              scriptdomain={PRODUCT_DOMAINS['claims']}
              import='claims-product@dev'
              {...sharedParcelProps}
            />
          }
        />
        <Route
          path='/additional-tools'
          element={<AdditionalTools isContentLoaded={!isContentLoading} />}
        />
        <Route
          path='/eligibility/*'
          element={
            <abyss-parcel
              scriptdomain={PRODUCT_DOMAINS['eligibility']}
              import='eligibility-product@dev'
              {...sharedParcelProps}
            />
          }
        />
        <Route
          path='/claims/trackit/*'
          element={
            <abyss-parcel
              scriptdomain={PRODUCT_DOMAINS['trackit']}
              import='trackit-product@dev'
              {...sharedParcelProps}
            />
          }
        />
        <Route
          path='/feeschedulelookup/*'
          element={
            <abyss-parcel
              scriptdomain={PRODUCT_DOMAINS['fee-schedule-lookup']}
              import='fee-schedule-lookup-product@dev'
              {...sharedParcelProps}
            />
          }
        />
        <Route
          path='/paan/trackit/*'
          element={
            <abyss-parcel
              scriptdomain={PRODUCT_DOMAINS['trackit']}
              import='trackit-product@dev'
              {...sharedParcelProps}
            />
          }
        />
        <Route
          path='/referrals/trackit/*'
          element={
            <abyss-parcel
              scriptdomain={PRODUCT_DOMAINS['trackit']}
              import='trackit-product@dev'
              {...sharedParcelProps}
            />
          }
        />
        <Route
          path='/trackit/*'
          element={
            <abyss-parcel
              scriptdomain={PRODUCT_DOMAINS['trackit']}
              import='trackit-product@dev'
              {...sharedParcelProps}
            />
          }
        />
        <Route
          path='/claim-submission/*'
          element={
            <abyss-parcel
              scriptdomain={PRODUCT_DOMAINS['claimsub']}
              import='claimsub-product@dev'
              {...sharedParcelProps}
            />
          }
        />
        <Route
          path='/referral/*'
          element={
            <abyss-parcel
              scriptdomain={PRODUCT_DOMAINS['referrals']}
              import='referrals-product@dev'
              {...sharedParcelProps}
            />
          }
        />
        <Route
          path='/documentlibrary/*'
          element={
            <abyss-parcel
              scriptdomain={PRODUCT_DOMAINS['documentlibrary']}
              import='documentlibrary-product@dev'
              {...sharedParcelProps}
            />
          }
        />
        {/* Mpp args cannot have the sharedParcelprops because the key arg breaks navigation */}
        <Route
          path='/mpp/*'
          element={
            <abyss-parcel
              scriptdomain={PRODUCT_DOMAINS['mpp']}
              import='mpp-product@dev'
              user={stringifiedUser}
              provider={stringifiedProvider}
              payer={stringifiedPayer}
              provider-preference-complete={providerPreferenceComplete}
            />
          }
        />
        <Route
          path='/onboard-pro/*'
          element={
            <abyss-parcel
              scriptdomain={PRODUCT_DOMAINS['obp']}
              import='obp-product@dev'
              {...sharedParcelProps}
            />
          }
        />
        <Route
          path='/accessmanagement/*'
          element={
            <abyss-parcel
              scriptdomain={PRODUCT_DOMAINS['digisec']}
              import='digisec-product@dev'
              {...sharedParcelProps}
            />
          }
        />
        <Route path='/app-search/*' element={<CoveoSearchPage />} />
        <Route
          path='/contact-us/network-help/*'
          element={<ContactUs isContentLoaded={!isContentLoading} />}
        />
        <Route
          path='/contact-us'
          element={<ContactUs isContentLoaded={!isContentLoading} />}
        />
        <Route path='/opt-out' element={<OptOut />} />
        <Route
          path='/paan/*'
          element={
            <abyss-parcel
              scriptdomain={PRODUCT_DOMAINS['paan']}
              import='paan-product@dev'
              {...sharedParcelProps}
            />
          }
        />
        <Route
          path='/report-hub/*'
          element={
            <abyss-parcel
              scriptdomain={PRODUCT_DOMAINS['report-hub']}
              import='report-hub-product@dev'
              {...sharedParcelProps}
            />
          }
        />
        <Route
          path='/uhcp-forms/*'
          element={
            <abyss-parcel
              scriptdomain={PRODUCT_DOMAINS['uhcp-forms']}
              import='uhcp-forms-product@dev'
              {...sharedParcelProps}
            />
          }
        />
        <Route
          path='/code-lookup/*'
          element={
            <abyss-parcel
              scriptdomain={PRODUCT_DOMAINS['eligibility']}
              import='code-lookup-product@dev'
              {...sharedParcelProps}
            />
          }
        />
        <Route path='/technicalUser' element={<TechnicalUserRedirect />} />
        <Route path='*' element={<NotFound />} />
      </Route>
    </ReactRoutes>
  );
};
